import { ProfileAccountInfo } from "api/interfaces/accountInterface.interface";
import {
  SelectedRowAccountInfo,
  SUBSCRIPTION_LOCATION,
  SUBSCRIPTION_TYPE,
  SubscriptionPageOutletContext,
} from "api/interfaces/subscription.interface";
import Tabs from "components/atoms/tabs/Tabs";
import Status, { StatusType } from "components/blocks/status/Status";
import { TableTemplate } from "components/templates/default/table/TableTemplate";
import { useMenu } from "hook/useMenu";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Navigate, Outlet, useLocation } from "react-router-dom";
import { useAppSelector } from "redux/hooks";
import { Option } from "utils/options";
import * as s from "pages/subscription/SubscriptionStyled";
import useTableQueryParams from "hook/useTableQueryParams";
import { LEVEL_TYPE } from "api/interfaces/commonInterface.interface";
import { useFlag } from "@unleash/proxy-client-react";
import { TitleComponentType } from "components/blocks/subheader/SubHeader";

const DwTabs = [
  {
    label: "Organization",
    value: SUBSCRIPTION_LOCATION.ORGANIZATION,
  },
  {
    label: "Keys",
    value: SUBSCRIPTION_LOCATION.KEYS,
  },
  {
    label: "Services & Pricing",
    value: SUBSCRIPTION_LOCATION.SERVICES,
  },
  {
    label: "DW Cumulus",
    value: SUBSCRIPTION_LOCATION.LYVE,
  },
];

const CpKeysTabs = [
  {
    label: "Organization",
    value: SUBSCRIPTION_LOCATION.KEYS_ORGANIZATION,
  },
  {
    label: "Keys",
    value: SUBSCRIPTION_LOCATION.KEYS_LIST,
  },
  {
    label: "Request History",
    value: SUBSCRIPTION_LOCATION.KEYS_REQUEST_HISTORY,
  },
];
const EuTabs = [
  {
    label: "Standard",
    value: SUBSCRIPTION_TYPE.STANDARD,
  },
  {
    label: "Storage",
    value: SUBSCRIPTION_TYPE.STORAGE,
  },
  {
    label: "Request History",
    value: "REQUEST",
  },
];

export default function SubscriptionPage(): JSX.Element {
  const location = useLocation();
  const [isCoTermOpen, setIsCoTermOpen] = useState<boolean>(false);
  const enableSubscriptionCumulusW = useFlag("feature_subscription_cumulus");
  const { menuNavigation } = useMenu({});
  const [tab, setTab] = useState<SUBSCRIPTION_TYPE | "REQUEST">(
    SUBSCRIPTION_TYPE.STANDARD
  );
  const [filters, setFilters] = useState<React.ReactNode>(null);
  const selectedAccount: ProfileAccountInfo = useAppSelector(
    (state) => state.accountSettings
  );
  const { planStorageUpdated } = useAppSelector(
    (state) => state.subscriptionStomp
  );
  const {
    searchParams,
    setSearchParams,
    updateQueryInfo,
    onClickSearch,
    onHeaderCell,
    onChangePage,
  } = useTableQueryParams();

  const { isDW, isCP, isEu } = useMemo(() => {
    return {
      isDW: selectedAccount.accountLevel === LEVEL_TYPE.DW,
      isCP: selectedAccount.accountLevel === LEVEL_TYPE.CP,
      isEu: selectedAccount.accountLevel === LEVEL_TYPE.EU,
    };
  }, [selectedAccount.accountLevel]);

  const selectedRowAccount: SelectedRowAccountInfo = useMemo(() => {
    const rowAccountId = searchParams.get("rowAccountId");
    const rowAccountLevel = searchParams.get("rowAccountLevel");
    const rowAccountNumber = searchParams.get("rowAccountNumber");
    const rowParentLevel = searchParams.get("rowParentLevel");
    const rowIsUsagePaid = searchParams.get("rowIsUsagePaid");
    if (rowAccountId && rowAccountNumber && rowAccountLevel && rowParentLevel) {
      return {
        accountId: rowAccountId,
        accountLevel: rowAccountLevel as LEVEL_TYPE,
        accountNumber: rowAccountNumber,
        parentLevel: rowParentLevel as LEVEL_TYPE,
        isUsagePaid: rowIsUsagePaid === "true" ? true : false,
      };
    }
    return {
      accountId: selectedAccount.accountId,
      accountLevel: selectedAccount.accountLevel,
      accountNumber: selectedAccount.accountNumber,
      parentLevel: selectedAccount.parentLevel as LEVEL_TYPE,
      isUsagePaid: selectedAccount.isUsagePaid,
    };
  }, [searchParams, selectedAccount]);

  const getBucketTitleComponent = useCallback(
    (pathname: string) => {
      if (location.pathname.includes(pathname)) {
        const label = "DW Cumulus";
        const bucketName = location.pathname.replace(pathname, "").slice(1);
        return [
          {
            label: label,
            onClick: () =>
              menuNavigation({
                to: pathname,
              }),
          },
          {
            label: bucketName,
          },
        ];
      }
      return <></>;
    },
    [location.pathname, menuNavigation]
  );

  // EU 계정 여부에 따라 Standard/Storage/Request History 탭 구성
  const getDefaultTabsComponent = useCallback(
    (isEU: boolean): TitleComponentType => {
      const filteredTabs = EuTabs.filter((tabItem) => {
        if (isEU) return true;
        return tabItem.value !== "REQUEST";
      });

      return (
        <Tabs
          tabs={filteredTabs}
          currentTab={tab}
          onChangeTab={(value) =>
            setTab(value as SUBSCRIPTION_TYPE | "REQUEST")
          }
        />
      );
    },
    [tab]
  );

  const getDwTitleComponent = useCallback((): TitleComponentType => {
    const pathname = location.pathname;

    if (pathname.includes("/organization/history")) return <></>;

    if (enableSubscriptionCumulusW) {
      if (pathname.includes(SUBSCRIPTION_LOCATION.LYVE)) {
        if (pathname.split("/").at(-1) !== "lyve") {
          return getBucketTitleComponent(SUBSCRIPTION_LOCATION.LYVE);
        }
        return "DW Cumulus";
      }
    }
    return DwTabs.find((tab) => tab.value === pathname)?.label;
  }, [location.pathname, enableSubscriptionCumulusW, getBucketTitleComponent]);

  const getCpTitleComponent = useCallback((): TitleComponentType => {
    const pathname = location.pathname;
    const isOrgHistory = pathname.includes("/organization/history");

    // Organization History는 선택된 row가 EU가 아니면 타이틀 없음
    if (isOrgHistory && selectedRowAccount?.isUsagePaid) return <></>;

    if (!isOrgHistory) {
      return (
        <Tabs
          tabs={CpKeysTabs as unknown as Option[]}
          currentTab={pathname}
          onChangeTab={(value) =>
            menuNavigation({
              to: value,
            })
          }
        />
      );
    }

    return getDefaultTabsComponent(false);
  }, [
    location.pathname,
    selectedRowAccount?.isUsagePaid,
    menuNavigation,
    getDefaultTabsComponent,
  ]);

  const getEuTitleComponent = useCallback((): TitleComponentType => {
    const pathname = location.pathname;
    const isOrgHistory = pathname.includes("/organization/history");
    const isEU = selectedAccount.accountLevel === LEVEL_TYPE.EU;
    const isNonEUSelectedRow =
      selectedRowAccount.accountLevel &&
      selectedRowAccount.accountLevel !== LEVEL_TYPE.EU;

    // Organization History는 선택된 row가 EU가 아니면 타이틀 없음
    if (isOrgHistory && isNonEUSelectedRow) return <></>;

    // EU + (CoTerm 또는 Usage 결제)면 타이틀 없음
    if (isEU && (isCoTermOpen || selectedAccount.isUsagePaid)) return <></>;

    return getDefaultTabsComponent(isEU);
  }, [
    location.pathname,
    selectedAccount.accountLevel,
    selectedAccount.isUsagePaid,
    selectedRowAccount.accountLevel,
    isCoTermOpen,
    getDefaultTabsComponent,
  ]);

  const titleComponent: TitleComponentType = useMemo(() => {
    if (isDW) return getDwTitleComponent();
    if (isCP) return getCpTitleComponent();
    return getEuTitleComponent();
  }, [
    isDW,
    isCP,
    getDwTitleComponent,
    getCpTitleComponent,
    getEuTitleComponent,
  ]);
  const keyword = useMemo(
    () => searchParams.get("keyword") ?? "",
    [searchParams]
  );

  const [status, setStatus] = useState<StatusType>({
    totalTitle: "Total",
    totalValue: 0,
  });

  const centerChildren = useMemo(() => {
    if (
      location.pathname.includes("/organization/history") ||
      location.pathname.includes("/services") ||
      location.pathname.includes("/usage/billing")
    ) {
      return null;
    }

    return <Status status={status} />;
  }, [location.pathname, status]);

  const [buttons, setButtons] = useState<React.ReactNode>(null);

  const onClickFilterTitle = useCallback(
    (value: string | undefined, name: string) => {
      if (value !== undefined) {
        updateQueryInfo({
          pageNum: 0,
          [name]: value === "all" ? null : value,
        });
      }
    },
    [updateQueryInfo]
  );

  useEffect(() => {
    if (
      location.pathname !== SUBSCRIPTION_LOCATION.KEYS_LIST &&
      location.pathname !== SUBSCRIPTION_LOCATION.KEYS
    ) {
      setFilters(null);
    }
  }, [location.pathname, searchParams, updateQueryInfo]);

  const noSearch = useMemo(() => {
    if (location.pathname.includes("/organization/history")) {
      if (isEu) {
        if (isCoTermOpen && tab === "REQUEST") {
          return true;
        }
        return tab !== "REQUEST";
      }
      return true;
    }
    return location.pathname.includes("/services");
  }, [location.pathname, isEu, isCoTermOpen, tab]);

  const outletContext: SubscriptionPageOutletContext = {
    updateQueryInfo,
    onHeaderCell,
    onChangePage,
    setStatus,
    setButtons,
    onClickFilterTitle,
    tab,
    selectedRowAccount,
    isCoTermOpen,
    setIsCoTermOpen,
    setFilters,
  };

  useEffect(() => {
    if (!location.pathname.includes("/subscription")) return;
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete("keyword");
        next.delete("pageNum");
        next.delete("pageLimit");
        next.delete("sortType");
        next.delete("sortDirection");
        next.delete("type");
        next.delete("fromDate");
        next.delete("toDate");
        next.delete("status");
        return next;
      },
      { replace: true }
    );
  }, [tab, location.pathname]);

  useEffect(() => {
    setIsCoTermOpen(false);
  }, [selectedAccount.accountLevel]);

  // storage plan이 조회 중인 row의 계정을 갱신하면 페이지를 다시 마운트해 하위 쿼리를 재조회
  const subscriptionRemountKey = useMemo(() => {
    return planStorageUpdated.updatedDate
      ? `storage-${planStorageUpdated.updatedDate.toISOString()}`
      : "default";
  }, [planStorageUpdated]);

  if (
    isCP &&
    (location.pathname === "/subscription/organization" ||
      location.pathname.startsWith("/subscription/organization/")) &&
    !location.pathname.startsWith("/subscription/keys")
  ) {
    const rest = location.pathname.slice("/subscription/organization".length);
    return <Navigate to={`/subscription/keys/organization${rest}`} replace />;
  }

  return (
    <s.UsagePlan.OutletWrapper
      key={subscriptionRemountKey}
      className={location.pathname.replaceAll("/", "_")}
      $level={selectedAccount.accountLevel}
    >
      <TableTemplate
        titleComponent={titleComponent}
        centerChildren={centerChildren}
        noSearch={noSearch}
        keyword={keyword}
        onClickSearch={onClickSearch}
        buttons={buttons}
        forwardSearch={filters}
      >
        <Outlet context={outletContext} />
      </TableTemplate>
    </s.UsagePlan.OutletWrapper>
  );
}
